import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, ArrowRight, Calculator, Check, Info, Search, ShieldCheck } from 'lucide-react';
import { LangLink } from '@/i18n/LangContext';
import { swissInsuranceReference2026 as ref } from '@/data/swissInsuranceReference';
import { TAX_YEAR } from '@/data/taxSavings3a';
import { searchLocations } from '@/lib/lamal';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://api.unity-consult.ch').replace(/\/$/, '');
const LOCATION_URL = '/data/lamal/locations.json';
const COLORS = { deep: '#183b36', green: '#168447', amber: '#c98a45', grid: '#dce6e1' };
const formatFrancs = (value) => `CHF ${new Intl.NumberFormat('it-CH', { maximumFractionDigits: 0 }).format(Math.round(Number(value) || 0))}`;
const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));
const maxWithoutPensionFund = (income) => Math.min(ref.pillar3aWithoutPensionFundMax, Math.floor(income * (ref.pillar3aWithoutPensionFundPercent / 100)));
const locationLabel = (location) => location ? `${location.z} ${location.l}${location.n !== location.l ? ` · ${location.n}` : ''} (${location.c})` : '';
const TaxTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3 text-sm shadow-lg">
      <p className="mb-2 font-semibold">{label}</p>
      {payload.map((item) => (
        <p key={item.dataKey || item.name} className="flex items-center justify-between gap-5 text-xs text-foreground/70">
          <span>{item.name}</span>
          <strong className="tabular-nums text-foreground">{formatFrancs(item.value)}</strong>
        </p>
      ))}
    </div>
  );
};

const ResidenceTypeButton = ({ active, onClick, title, text }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`rounded-xl border p-4 text-left transition-colors ${active ? 'border-primary bg-primary text-white shadow-sm' : 'border-border bg-white text-foreground hover:border-primary'}`}
  >
    <span className="flex items-center justify-between gap-3 text-sm font-bold">
      {title}
      <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${active ? 'border-white bg-white text-primary' : 'border-border'}`}>
        {active && <Check className="h-3.5 w-3.5" />}
      </span>
    </span>
    <span className={`mt-1.5 block text-xs leading-relaxed ${active ? 'text-white/75' : 'text-muted-foreground'}`}>{text}</span>
  </button>
);

const StepProgress = ({ step, onStepChange }) => {
  const steps = ['Situazione', 'Dati', 'Risultato'];
  return (
    <ol className="grid grid-cols-3 gap-2" aria-label="Avanzamento del calcolatore">
      {steps.map((label, index) => {
        const number = index + 1;
        const active = number === step;
        const complete = number < step;
        return (
          <li key={label}>
            <button
              type="button"
              onClick={() => complete && onStepChange(number)}
              disabled={number > step}
              aria-current={active ? 'step' : undefined}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[11px] font-bold transition-colors sm:text-xs ${active ? 'bg-primary text-white' : complete ? 'bg-brand-pale text-primary hover:bg-brand-light/55' : 'bg-background text-muted-foreground'}`}
            >
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] ${active ? 'bg-white text-primary' : complete ? 'bg-primary text-white' : 'border border-border bg-white'}`}>
                {complete ? <Check className="h-3 w-3" /> : number}
              </span>
              <span className="truncate">{label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};

export const TaxSavingsCalculator = ({ compact = false, source = 'page' }) => {
  const [step, setStep] = useState(1);
  const [residenceType, setResidenceType] = useState('ordinary');
  const [taxableIncome, setTaxableIncome] = useState(85000);
  const [annualContribution, setAnnualContribution] = useState(ref.pillar3aWithPensionFundMax);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [locations, setLocations] = useState([]);
  const [dataStatus, setDataStatus] = useState('loading');
  const [hasPensionFund, setHasPensionFund] = useState(true);
  const [result, setResult] = useState(null);
  const [calculationStatus, setCalculationStatus] = useState('idle');
  const [calculationError, setCalculationError] = useState('');
  const [showChart, setShowChart] = useState(false);
  const [municipalityQuery, setMunicipalityQuery] = useState('');
  const [municipalitySearchOpen, setMunicipalitySearchOpen] = useState(false);
  const [activeMunicipalityIndex, setActiveMunicipalityIndex] = useState(-1);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    const loadLocations = async () => {
      try {
        setDataStatus('loading');
        const response = await fetch(LOCATION_URL, { signal: controller.signal, cache: 'force-cache' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!Array.isArray(payload) || !payload.length) throw new Error('Località non disponibili');
        if (disposed) return;
        setLocations(payload);
        setDataStatus('complete');
      } catch (error) {
        if (disposed || error?.name === 'AbortError') return;
        setDataStatus('error');
      }
    };
    loadLocations();
    return () => {
      disposed = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const updateChartVisibility = () => setShowChart(mediaQuery.matches);
    updateChartVisibility();
    mediaQuery.addEventListener('change', updateChartVisibility);
    return () => mediaQuery.removeEventListener('change', updateChartVisibility);
  }, []);

  const municipalityResults = useMemo(() => searchLocations(locations, municipalityQuery, 8), [locations, municipalityQuery]);
  const isPermitB = residenceType === 'permit_b';
  const maxDeductible = hasPensionFund
    ? ref.pillar3aWithPensionFundMax
    : maxWithoutPensionFund(taxableIncome);
  const deductibleContribution = result?.deductible_contribution || 0;
  const taxBefore = result?.tax_before || 0;
  const taxAfter = result?.tax_after || 0;
  const taxSaving = result?.tax_saving || 0;
  const effectiveReturn = deductibleContribution > 0 ? (taxSaving / deductibleContribution) * 100 : 0;
  const marginalRate = deductibleContribution > 0 ? taxSaving / deductibleContribution : 0;
  // Il risultato per il permesso B descrive la tassazione ordinaria successiva,
  // non il rimborso effettivo dell'imposta trattenuta alla fonte.
  const resultLabel = isPermitB
    ? 'Beneficio stimato in tassazione ordinaria (Permesso B)'
    : 'Risparmio fiscale stimato';
  const chartData = useMemo(() => ([
    { name: 'Senza 3a', value: taxBefore, color: COLORS.deep },
    { name: 'Con 3a', value: taxAfter, color: COLORS.green },
    { name: 'Risparmio', value: taxSaving, color: COLORS.amber },
  ]), [taxBefore, taxAfter, taxSaving]);

  const selectMunicipality = (municipality) => {
    setSelectedMunicipality(municipality);
    setMunicipalityQuery(locationLabel(municipality));
    setMunicipalitySearchOpen(false);
    setActiveMunicipalityIndex(-1);
  };

  const updateTaxableIncome = (value) => {
    const income = Math.floor(clamp(value, 0, 300000));
    setTaxableIncome(income);
    if (!hasPensionFund) {
      setAnnualContribution((contribution) => Math.min(contribution, maxWithoutPensionFund(income)));
    }
  };

  const calculate = async () => {
    if (!selectedMunicipality) {
      setCalculationError('Seleziona un comune di domicilio dall’elenco.');
      return;
    }
    setCalculationError('');
    setCalculationStatus('loading');
    setResult(null);
    setStep(3);
    try {
      const response = await fetch(`${API_BASE_URL}/public/v1/tax-savings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bfs_id: selectedMunicipality.b,
          postal_code: selectedMunicipality.z,
          locality: selectedMunicipality.l,
          municipality: selectedMunicipality.n,
          canton: selectedMunicipality.c,
          taxable_income_canton: taxableIncome,
          taxable_income_federal: taxableIncome,
          annual_contribution: annualContribution,
          has_pension_fund: hasPensionFund,
          net_earned_income: hasPensionFund ? null : taxableIncome,
          relationship: 'single',
          confession: 4,
          spouse_confession: null,
          children_ages: [],
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.detail || 'Calcolo non disponibile.');
      setResult(body);
      setCalculationStatus('complete');
    } catch (error) {
      setCalculationError(error.message || 'Calcolo non disponibile.');
      setCalculationStatus('error');
    }
  };

  const handleMunicipalityKeyDown = (event) => {
    if (!municipalityResults.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMunicipalitySearchOpen(true);
      setActiveMunicipalityIndex((index) => Math.min(index + 1, municipalityResults.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMunicipalitySearchOpen(true);
      setActiveMunicipalityIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && municipalitySearchOpen && activeMunicipalityIndex >= 0) {
      event.preventDefault();
      selectMunicipality(municipalityResults[activeMunicipalityIndex]);
    } else if (event.key === 'Escape') {
      setMunicipalitySearchOpen(false);
      setActiveMunicipalityIndex(-1);
    }
  };

  return (
    <article data-testid={`tax-savings-calculator-${source}`} className={`overflow-hidden rounded-2xl border border-border bg-white ${compact ? '' : 'shadow-[0_24px_70px_-45px_rgba(10,61,32,0.45)]'}`}>
      <div className={`border-b border-border bg-gradient-to-br from-brand-pale/70 via-white to-white ${compact ? 'p-4 sm:p-5' : 'p-4 sm:p-6'}`}>
        <div className="flex items-center gap-3">
          <span className="rounded-lg bg-primary p-2 text-white"><Calculator className="h-5 w-5" /></span>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary sm:text-xs">Calcolatore fiscale 3a</p>
            <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Quanto puoi risparmiare?</h2>
          </div>
        </div>
        {!compact && <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground/70">Tre passaggi rapidi per stimare la deduzione 3a in base alla tua situazione e al comune di domicilio.</p>}
        <div className="mt-4"><StepProgress step={step} onStepChange={setStep} /></div>
      </div>

      <div className={compact ? 'p-4 sm:p-5' : 'p-4 sm:p-6 md:p-7'}>
        {step === 1 && (
          <section data-testid="tax-step-1" aria-labelledby={`tax-step-1-title-${source}`} className="mx-auto max-w-3xl">
            <div className="mb-4 text-center sm:mb-5">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Passaggio 1 di 3</p>
              <h3 id={`tax-step-1-title-${source}`} className="mt-1 font-heading text-xl font-bold tracking-tight sm:text-2xl">Qual è la tua situazione fiscale?</h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label="Tipo di tassazione">
              <ResidenceTypeButton active={isPermitB} onClick={() => setResidenceType('permit_b')} title="Permesso B" text="Imposta alla fonte: stima dell’effetto nella tassazione ordinaria." />
              <ResidenceTypeButton active={residenceType === 'ordinary'} onClick={() => setResidenceType('ordinary')} title="Svizzero / Permesso C" text="Dichiarazione fiscale ordinaria." />
            </div>
            <button type="button" onClick={() => setStep(2)} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-dark sm:ml-auto sm:flex sm:w-fit">
              Continua <ArrowRight className="h-4 w-4" />
            </button>
          </section>
        )}

        {step === 2 && (
          <section data-testid="tax-step-2" aria-labelledby={`tax-step-2-title-${source}`} className="mx-auto max-w-4xl">
            <div className="mb-3 sm:mb-4">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Passaggio 2 di 3</p>
              <h3 id={`tax-step-2-title-${source}`} className="mt-1 font-heading text-xl font-bold tracking-tight sm:text-2xl">Inserisci i dati per la stima</h3>
            </div>
            <div className="grid gap-3 md:grid-cols-2 md:gap-5">
              <div className="rounded-xl bg-brand-pale/35 p-3 sm:p-4">
                <label htmlFor={`tax-income-number-${source}`} className="flex items-center justify-between gap-3 text-sm font-semibold">
                  <span>Reddito imponibile cantonale annuo</span>
                  <output htmlFor={`tax-income-${source}`} className="text-primary tabular-nums">{formatFrancs(taxableIncome)}</output>
                </label>
                <input id={`tax-income-${source}`} aria-label="Reddito imponibile cantonale annuo" type="range" min="0" max="300000" step="1000" value={taxableIncome} onChange={(event) => updateTaxableIncome(event.target.value)} className="mt-3 hidden w-full accent-primary sm:block" />
                <div className="relative mt-3 sm:mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">CHF</span>
                  <input id={`tax-income-number-${source}`} type="number" min="0" max="300000" step="1000" value={taxableIncome} onChange={(event) => updateTaxableIncome(event.target.value)} className="w-full rounded-lg border border-input bg-white py-2.5 pl-12 pr-3 text-sm tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25" />
                </div>
              </div>

              <div className="block rounded-xl bg-brand-pale/35 p-3 sm:p-4">
                <label htmlFor={`tax-municipality-${source}`} className="block text-sm font-semibold">CAP o comune di domicilio</label>
                <div
                  className="relative mt-3"
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setMunicipalitySearchOpen(false);
                      setActiveMunicipalityIndex(-1);
                    }
                  }}
                >
                  <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    id={`tax-municipality-${source}`}
                    type="search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={municipalitySearchOpen && !selectedMunicipality && municipalityQuery.trim().length >= 2}
                    aria-controls={`tax-municipality-results-${source}`}
                    aria-activedescendant={activeMunicipalityIndex >= 0 ? `tax-municipality-option-${source}-${activeMunicipalityIndex}` : undefined}
                    value={municipalityQuery}
                    onChange={(event) => {
                      setMunicipalityQuery(event.target.value);
                      setSelectedMunicipality(null);
                      setMunicipalitySearchOpen(true);
                      setActiveMunicipalityIndex(-1);
                    }}
                    onFocus={() => !selectedMunicipality && municipalityQuery.trim().length >= 2 && setMunicipalitySearchOpen(true)}
                    onKeyDown={handleMunicipalityKeyDown}
                    placeholder="Es. 6900 oppure Lugano"
                    autoComplete="off"
                    className="w-full rounded-lg border border-input bg-white py-2.5 pl-10 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                  {municipalitySearchOpen && !selectedMunicipality && municipalityQuery.trim().length >= 2 && (
                    <div id={`tax-municipality-results-${source}`} role="listbox" className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-white p-1 shadow-xl">
                      {municipalityResults.length ? municipalityResults.map((option, index) => (
                        <button
                          key={`${option.z}-${option.l}-${option.b}`}
                          id={`tax-municipality-option-${source}-${index}`}
                          type="button"
                          role="option"
                          aria-selected={selectedMunicipality?.b === option.b && selectedMunicipality?.z === option.z}
                          onMouseEnter={() => setActiveMunicipalityIndex(index)}
                          onClick={() => selectMunicipality(option)}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${index === activeMunicipalityIndex ? 'bg-brand-pale text-primary' : 'hover:bg-muted'}`}
                        >
                          <span className="font-medium">{option.z} {option.l}</span>
                          <span className="text-xs text-muted-foreground">{option.n} · {option.c}</span>
                        </button>
                      )) : (
                        <p className="px-3 py-3 text-sm text-muted-foreground">Nessun comune trovato.</p>
                      )}
                    </div>
                  )}
                </div>
                {dataStatus === 'loading' && <p className="mt-1 text-xs text-muted-foreground">Caricamento dei comuni…</p>}
                {dataStatus === 'error' && <p className="mt-1 text-xs text-red-700">Elenco dei CAP non disponibile. Ricarica la pagina.</p>}
                <p className="mt-2 text-xs text-muted-foreground">Alcuni CAP comprendono più comuni: scegli la riga corrispondente al tuo domicilio.</p>
              </div>

              <div className="rounded-xl bg-brand-pale/35 p-3 sm:p-4">
                <label htmlFor={`tax-contribution-${source}`} className="flex items-center justify-between gap-3 text-sm font-semibold">
                  <span>Versamento annuo nel 3a</span>
                  <output htmlFor={`tax-contribution-${source}`} className="text-primary tabular-nums">{formatFrancs(annualContribution)}</output>
                </label>
                <input id={`tax-contribution-${source}`} type="range" min="0" max={maxDeductible} step="1" value={annualContribution} onChange={(event) => setAnnualContribution(Number(event.target.value))} className="mt-4 w-full accent-primary" />
                <div className="relative mt-3">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">CHF</span>
                  <input id={`tax-contribution-number-${source}`} type="number" aria-label="Versamento annuo nel 3a in franchi" min="0" max={maxDeductible} step="1" value={annualContribution} onChange={(event) => setAnnualContribution(Math.floor(clamp(event.target.value, 0, maxDeductible)))} className="w-full rounded-lg border border-input bg-white py-2.5 pl-12 pr-3 text-sm tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25" />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Limite deducibile {TAX_YEAR}: {formatFrancs(maxDeductible)}</p>
              </div>

              <div className="rounded-xl bg-brand-pale/35 p-3 sm:p-4">
                <p className="text-sm font-semibold">Situazione previdenziale</p>
                <div className="mt-3 grid grid-cols-2 gap-2" role="group" aria-label="Situazione previdenziale">
                  <button type="button" aria-pressed={hasPensionFund} onClick={() => { setHasPensionFund(true); setAnnualContribution((value) => Math.min(value, ref.pillar3aWithPensionFundMax)); }} className={`rounded-lg border px-3 py-2.5 text-xs font-semibold transition-colors sm:text-sm ${hasPensionFund ? 'border-primary bg-primary text-white' : 'border-border bg-white text-foreground hover:border-primary'}`}>Con cassa pensione</button>
                  <button type="button" aria-pressed={!hasPensionFund} onClick={() => { setHasPensionFund(false); setAnnualContribution((value) => Math.min(value, maxWithoutPensionFund(taxableIncome))); }} className={`rounded-lg border px-3 py-2.5 text-xs font-semibold transition-colors sm:text-sm ${!hasPensionFund ? 'border-primary bg-primary text-white' : 'border-border bg-white text-foreground hover:border-primary'}`}>Senza cassa pensione</button>
                </div>
                {!hasPensionFund && <p className="mt-3 text-xs leading-relaxed text-muted-foreground">Per stimare il limite 3a usiamo il reddito imponibile cantonale inserito sopra come riferimento. Il limite effettivo dipende dal reddito netto da attività lucrativa.</p>}
              </div>
            </div>
            {isPermitB && (
              <p className="mt-4 flex items-start gap-2 rounded-xl border border-primary/15 bg-brand-pale/50 p-3 text-xs leading-relaxed text-foreground/75"><Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Per il permesso B il calcolo riguarda la tassazione ordinaria successiva; non indica quanto sarà rimborsato dell’imposta alla fonte già trattenuta. Verifica con il tuo cantone la procedura per dedurre il 3a.</p>
            )}
            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground"><Info className="mt-0.5 h-4 w-4 shrink-0" />Inserisci il reddito imponibile, non il salario lordo. La stima assume una persona sola senza figli a carico, nessuna imposta ecclesiastica e lo stesso imponibile per Confederazione e cantone. Senza cassa pensione, il limite 3a è stimato dallo stesso reddito. Il calcolo usa le tariffe AFC {TAX_YEAR}.</p>
            {calculationError && step === 2 && <p role="alert" className="mt-3 text-sm text-red-700">{calculationError}</p>}
            <div className="mt-4 flex gap-2 sm:mt-5 sm:justify-end">
              <button type="button" onClick={() => setStep(1)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-primary/25 bg-white px-4 py-3 text-sm font-bold text-primary transition-colors hover:bg-brand-pale"><ArrowLeft className="h-4 w-4" /> Indietro</button>
              <button type="button" onClick={calculate} disabled={!selectedMunicipality || calculationStatus === 'loading'} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none">Calcola <ArrowRight className="h-4 w-4" /></button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section data-testid="tax-step-3" aria-labelledby={`tax-step-3-title-${source}`} className="mx-auto max-w-5xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Passaggio 3 di 3</p>
            <h3 id={`tax-step-3-title-${source}`} className="mt-1 font-heading text-xl font-bold tracking-tight sm:text-2xl">La tua stima</h3>
            {calculationStatus === 'loading' && <p role="status" className="mt-5 rounded-xl bg-brand-pale p-4 text-sm">Calcolo delle imposte con le tariffe AFC {TAX_YEAR} in corso…</p>}
            {calculationStatus === 'error' && <div role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900"><p>{calculationError}</p><p className="mt-2">Nessun importo viene mostrato finché il calcolo non è verificato. Puoi <a className="underline" href="https://swisstaxcalculator.estv.admin.ch/#/calculator/income-wealth-tax" target="_blank" rel="noreferrer">usare il calcolatore AFC</a> oppure riprovare più tardi.</p></div>}
            {result && <>
            <div className="mt-4 grid gap-3 md:grid-cols-3" aria-live="polite">
              <div className="rounded-xl bg-brand-deep p-4 text-white sm:p-5 md:col-span-2">
                <p className="text-xs text-white/65">{resultLabel}</p>
                <p data-testid="tax-saving-result" className="mt-1 font-heading text-3xl font-bold tabular-nums sm:text-5xl">{formatFrancs(taxSaving)}</p>
                <p className="mt-2 text-xs leading-relaxed text-white/75 sm:text-sm">Con un versamento deducibile di {formatFrancs(deductibleContribution)} a {result.municipality} ({result.canton}). <span className="md:hidden">Rendimento fiscale: {effectiveReturn.toFixed(1)}%.</span></p>
              </div>
              <div className="hidden rounded-xl border border-border bg-white p-5 md:block">
                <p className="text-xs text-muted-foreground">Beneficio fiscale sul versamento</p>
                <p className="mt-1 font-heading text-3xl font-bold text-primary tabular-nums">{effectiveReturn.toFixed(1)}%</p>
                <p className="mt-2 text-xs text-foreground/55">Circa CHF {(marginalRate * 100).toFixed(0)} di imposte in meno ogni CHF 100 dedotti.</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="col-span-2 rounded-xl bg-brand-pale/65 p-3 sm:col-span-1"><p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">Comune</p><p className="mt-1 font-heading text-base font-bold">{result.municipality} ({result.canton})</p></div>
              <div className="rounded-xl bg-brand-pale/65 p-3"><p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">Prima del 3a</p><p className="mt-1 font-heading text-base font-bold tabular-nums">{formatFrancs(taxBefore)}</p></div>
              <div className="rounded-xl bg-brand-pale/65 p-3"><p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">Dopo il 3a</p><p className="mt-1 font-heading text-base font-bold tabular-nums">{formatFrancs(taxAfter)}</p></div>
            </div>
            {isPermitB && (
              <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-950">Per il permesso B la differenza calcolata non equivale al rimborso: l’imposta alla fonte già versata e la procedura cantonale determinano il conguaglio effettivo. Verifica il termine applicabile per la richiesta.</p>
            )}
            {showChart && (
              <div className="mt-5 h-[230px]" role="img" aria-label="Grafico a barre con imposte stimate prima e dopo il versamento nel pilastro 3a e risparmio fiscale stimato.">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}><BarChart data={chartData} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}><CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tickFormatter={(value) => `${Math.round(value / 1000)}k`} tick={{ fontSize: 11 }} width={54} /><Tooltip content={<TaxTooltip />} /><Bar dataKey="value" name="Importo" radius={[6, 6, 0, 0]}>{chartData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}</Bar></BarChart></ResponsiveContainer>
              </div>
            )}
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">Fonte: {result.source}, anno fiscale {result.tax_year}. Differenza tra le imposte sul reddito calcolate prima e dopo la deduzione 3a; patrimonio, altri redditi e deduzioni non inseriti possono modificare l’imposta effettiva.</p>
            </>}
            <div className="mt-4 rounded-xl bg-brand-pale/70 p-3 sm:mt-5 sm:flex sm:items-center sm:justify-between sm:gap-4 sm:p-4">
              <div><p className="flex items-center gap-2 font-heading text-base font-bold tracking-tight"><ShieldCheck className="h-5 w-5 text-primary" /> Vuoi verificare il risultato?</p><p className="mt-1 hidden text-xs leading-relaxed text-foreground/65 sm:block">Controlliamo deducibilità, soluzione 3a e coerenza con il tuo piano previdenziale.</p></div>
              <LangLink to="/contatti" className="mt-3 inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-dark sm:mt-0 sm:w-auto">Chiedi una consulenza <ArrowRight className="h-4 w-4" /></LangLink>
            </div>
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button type="button" onClick={() => setStep(2)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-primary/25 bg-white px-4 py-3 text-sm font-bold text-primary transition-colors hover:bg-brand-pale"><ArrowLeft className="h-4 w-4" /> Modifica i dati</button>
              {source !== 'dedicated-page' && <LangLink to="/calcola-risparmio-fiscale" className="inline-flex items-center justify-center gap-2 rounded-lg border border-primary/25 bg-white px-4 py-3 text-sm font-bold text-primary transition-colors hover:bg-brand-pale">Apri il calcolatore completo <ArrowRight className="h-4 w-4" /></LangLink>}
            </div>
          </section>
        )}
      </div>
    </article>
  );
};

export default TaxSavingsCalculator;
